import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { Buffer } from "buffer";

//Core Components
import MDCButton from "../../core/MDCButton";
import MDCBrowseFile from "../../core/MDCBrowseFile";
import MDCLabel from "../../core/MDCLabel";
import DeleteConfirmationDialog from "../../shared/DeleteConfirmationDialog";
import ErrorText from "../../shared/ErrorText";

// Constants
import {
  FILE_EXCEEDS_10MB_ERROR,
  FILE_PDF_FORMAT_ERROR,
  MAX_10MB_FILE_SIZE,
  ADDRESS_PROOF_DESCRIPTION,
  ADDRESS_PROOF_COMPANY_EXAMPLES_LIST,
  ADDRESS_PROOF_COMPANY_EXAMPLES_TITLE,
  PDF_ALLOWED_EXTENSION,
  rrcdocumentNames,
} from "../../../constants/ManualRequestFormConstants";

// Helpers
import { getFileExtension } from "../../../helpers/fileExtension";
import { getBase64String } from "../../../helpers/utils";

// Material UI
import Grid from "@mui/material/Grid";

// Icon Components
import FileUploadIcon from "../../../components/iconComponents/FileUploadIcon";
import DiscardIcon from "../../../components/iconComponents/DiscardIcon";
import VollmachtsRelevantIcon from "../../core/VollmachtsRelevantIcon";
import DownloadFileIcon from "../../../components/iconComponents/DownloadFileIcon";

// Theme
import theme from "../../../themes/mdc-theme";

const AddressProofUpload = ({
  error,
  disabled,
  columnTitle,
  handleChange,
  addressDocumentVerifiedOn,
  addressDocumentVerifiedBy,
  addressDocumentType,
  isQESIconDisplay,
  uploadedFileData,
  isVollmachtCompleted,
  documentName,
  isOptional = false,
  isPPOAorSelfEmployeed,
}) => {
  const [openLoschenPopup, setOpenLoschenPopup] = useState(false);
  const [fileError, setFileError] = useState("");
  // const isPPOAorSelfEmployeed =
  //   isPassportUploadSelectedForJP || isVollmachtsPOAForm;
  let inputElement = useRef(null);

  useEffect(() => {
    setFileError(error || "");
  }, [error]);

  const handleFiles = (event) => {
    const fileData = event.target?.files[0];
    const fileName = event.target?.files[0]?.name;
    if (PDF_ALLOWED_EXTENSION.indexOf(getFileExtension(fileName)) === -1) {
      setFileError(FILE_PDF_FORMAT_ERROR);
    } else {
      if (event.target.files[0].size > MAX_10MB_FILE_SIZE) {
        // max file size is 10 MB.
        setFileError(FILE_EXCEEDS_10MB_ERROR);
      } else if (
        PDF_ALLOWED_EXTENSION.indexOf(getFileExtension(fileName)) > -1 && //Checking PDF extension
        fileData
      ) {
        //Converting Blob data in base 64 encoded string to send it as a json object in Axios API call
        getBase64String(fileData).then((resp) => {
          const base64File = resp.replace("data:application/pdf;base64,", ""); // removing base64 header from encoded string

          const fileEventObj = {
            target: {
              name: documentName,
              value: base64File,
            },
          };
          const addressDocumentTypeEvent = {
            target: {
              name: "addressDocumentType",
              value: addressDocumentType || "",
            },
          };
          fileError?.length && setFileError("");
          handleChange(fileEventObj);
          if (documentName === "addressDocument") {
            handleChange(addressDocumentTypeEvent);
          }
        });
      }
    }
  };

  const deleteUploadedFile = () => {
    handleChange({
      target: {
        name: documentName,
        value: "",
      },
    });
    if (documentName === "addressDocument") {
      handleChange({
        target: {
          name: "addressDocumentVerifiedOn",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "addressDocumentVerifiedBy",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "addressDocumentType",
          value: addressDocumentType || "",
        },
      });
      handleChange({
        target: { name: "addressDocumentCheckbox", value: false },
      });
    }
    setOpenLoschenPopup(false);
  };

  const downloadDocumentFile = () => {
    const base64DecodedData = Buffer.from(uploadedFileData, "base64");
    const documentBlobData = new Blob([base64DecodedData], {
      type: "application/octet-stream",
    });
    const documentBlobDataURL = URL.createObjectURL(documentBlobData);
    const link = document.createElement("a");
    link.href = documentBlobDataURL;
    link.setAttribute(
      "download",
      `${rrcdocumentNames[documentName].fileName}.pdf`
    );
    document.body.appendChild(link);
    link.click();
  };

  return (
    <>
      <label className={columnTitle}>
        {isOptional ? "Adressnachweis (optional)" : "Adressnachweis"}
        {isQESIconDisplay && (
          <VollmachtsRelevantIcon isQESCompleted={isVollmachtCompleted} />
        )}
      </label>

      <p>{ADDRESS_PROOF_DESCRIPTION}</p>
      {/* Document upload section  */}
      {uploadedFileData ? (
        <Grid container>
          <Grid container marginY="1rem" sx={{ cursor: "pointer" }}>
            <Grid item xs={7} display="flex" alignItems="end" marginY={".5rem"}>
              <Grid onClick={downloadDocumentFile} sx={{ cursor: "pointer" }}>
                <DownloadFileIcon color="#00C4DE" />
              </Grid>
              <Grid sx={{ marginLeft: "1rem" }}>
                <MDCLabel label="Adressnachweis hochgeladen" isReadOnly />
              </Grid>
            </Grid>
            <Grid item xs={5} textAlign="end">
              <MDCButton
                variant="outlined"
                label={"Löschen"}
                onClick={() => setOpenLoschenPopup(true)}
                endIcon={
                  <DiscardIcon
                    color={
                      disabled
                        ? theme?.palette?.MDCColors?.textColor
                        : theme?.palette?.MDCColors?.color23
                    }
                  />
                }
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid container marginY="1rem" flexDirection={"column"}>
          <>
            <MDCButton
              variant="outlined"
              label="Adressnachweis hochladen"
              endIcon={<FileUploadIcon />}
              onClick={() => inputElement.click()}
              disabled={disabled}
            />
            <MDCBrowseFile
              acceptFileFormats=".pdf"
              inputRef={(input) => (inputElement = input)}
              onChange={(e) => handleFiles(e)}
            />
          </>
        </Grid>
      )}
      {isPPOAorSelfEmployeed && (
        <Grid marginTop="0.5rem">
          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            {ADDRESS_PROOF_COMPANY_EXAMPLES_TITLE}
          </p>
          <p style={{ marginTop: "0.5rem", fontSize: "0.95rem" }}>
            {ADDRESS_PROOF_COMPANY_EXAMPLES_LIST}
          </p>
        </Grid>
      )}
      {uploadedFileData?.length && addressDocumentVerifiedOn?.length ? (
        <Grid marginTop={"1rem"}>
          Verifiziert am{" "}
          {moment(addressDocumentVerifiedOn).format("DD.MM.YYYY")}{" "}
          {moment(addressDocumentVerifiedOn).format("HH:mm:ss")} Uhr von{" "}
          {addressDocumentVerifiedBy}
        </Grid>
      ) : (
        <></>
      )}
      {/* Error message */}
      <ErrorText error={fileError} />
      {/* File delete Popup */}
      <DeleteConfirmationDialog
        show={openLoschenPopup}
        handlePopupClose={() => setOpenLoschenPopup(false)}
        handlePopupSubmit={deleteUploadedFile}
        headerTitle={"Upload löschen"}
        confirmationMessage={
          "Sind Sie sicher, dass Sie dieses Dokument löschen möchten?"
        }
      />
    </>
  );
};

export default AddressProofUpload;
